/**
 * Ideas - Full listing of all ideas (cottagecore redesign)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdeas, useDeleteIdea } from '../hooks/api/useIdeas';
import { useApp } from '../contexts/AppContext';
import { formatTaskDate } from '../utils/dateUtils';
import { previewText, cleanName } from '../utils/textUtils';
import DeleteIdeaConfirmModal from '../components/DeleteIdeaConfirmModal';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  moss:       '#6B7A3E',
  capRed:     '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// ─── Badge component ──────────────────────────────────────────────────────────
const Badge = ({ color, children }) => {
  const colors = {
    moss:   { bg: '#6B7A3E', text: '#fff' },
    orange: { bg: '#EA7C1F', text: '#fff' },
    ink:    { bg: '#2E2109', text: '#fff' },
    red:    { bg: '#C14A33', text: '#fff' },
  };
  const c = colors[color] || colors.ink;
  return (
    <span style={{
      background: c.bg,
      color: c.text,
      fontSize: 11,
      fontWeight: 800,
      padding: '2px 8px',
      borderRadius: T.full,
      letterSpacing: 0.3,
      fontFamily: T.sans,
    }}>
      {children}
    </span>
  );
};

// ─── Status badge helper ──────────────────────────────────────────────────────
const statusBadge = (status) => {
  if (!status) return null;
  if (status === 'active')        return <Badge color="moss">active</Badge>;
  if (status === 'in_discovery')  return <Badge color="orange">in discovery</Badge>;
  if (status === 'draft')         return <Badge color="ink">draft</Badge>;
  // fallback for any other status
  return <Badge color="ink">{status.replace(/_/g, ' ')}</Badge>;
};

// ─── Skeleton card ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div style={{
    background: T.paperDeep,
    borderRadius: T.r4,
    padding: 20,
    minHeight: 220,
    animation: 'cahoots-pulse 1.4s ease-in-out infinite',
  }} />
);

// ─── Idea card ────────────────────────────────────────────────────────────────
const IdeaCard = ({ idea, onClick, onRequestDelete, deleting }) => {
  const [hovered, setHovered] = React.useState(false);
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (deleting) return;
    onRequestDelete?.(idea);
  };

  const hasDrift = idea.drift_summary && (
    idea.drift_summary.drifted_count > 0 ||
    idea.drift_summary.total_drift_items > 0
  );
  const gapCount = idea.gap_count || 0;
  const repo = idea.github_repo || idea.repo_url || null;
  const lastTouched = idea.updated_at || idea.created_at;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.card,
        borderRadius: T.r4,
        boxShadow: hovered
          ? '0 4px 0 rgba(46,33,9,0.06), 0 16px 28px -8px rgba(46,33,9,0.18)'
          : T.sh2,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minHeight: 220,
        cursor: 'pointer',
        transition: 'box-shadow 0.18s ease, transform 0.18s ease',
        transform: hovered ? 'translateY(-2px)' : 'none',
        fontFamily: T.sans,
        position: 'relative',
      }}
    >
      {/* Delete button (shown on hover) */}
      {onRequestDelete && (
        <button
          type="button"
          onClick={handleDeleteClick}
          disabled={deleting}
          aria-label="Delete idea"
          title="Delete idea"
          style={{
            position: 'absolute',
            top: 12, right: 12,
            width: 28, height: 28,
            border: 'none',
            background: hovered ? 'rgba(193,74,51,0.10)' : 'transparent',
            color: hovered ? '#C14A33' : T.inkFaint,
            borderRadius: 6,
            cursor: deleting ? 'default' : 'pointer',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.12s, background 0.12s, color 0.12s',
            fontSize: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: T.sans,
            zIndex: 1,
          }}
        >
          ✕
        </button>
      )}

      {/* Top row: badges */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {statusBadge(idea.status)}
        {hasDrift && <Badge color="red">drift detected</Badge>}
        {gapCount > 0 && <Badge color="red">{gapCount} gap{gapCount !== 1 ? 's' : ''}</Badge>}
      </div>

      {/* Middle: title + one-liner */}
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 19,
          fontWeight: 800,
          color: T.ink,
          letterSpacing: -0.3,
          lineHeight: 1.2,
          fontFamily: T.sans,
        }}>
          {cleanName(idea.name) || 'Untitled Idea'}
        </div>
        {idea.idea && (
          <div style={{
            fontSize: 13,
            color: T.ink2,
            lineHeight: 1.5,
            marginTop: 6,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {previewText(idea.idea, 160)}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        marginTop: 'auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 10,
        borderTop: `1px dashed ${T.rule}`,
        gap: 8,
      }}>
        <span style={{
          fontFamily: T.mono,
          fontSize: 11,
          color: T.inkMuted,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '55%',
        }}>
          {repo || '\u00A0'}
        </span>
        <span style={{
          fontFamily: T.mono,
          fontSize: 11,
          color: T.inkMuted,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          {lastTouched ? formatTaskDate(lastTouched) : ''}
        </span>
      </div>
    </div>
  );
};

// ─── Create card ──────────────────────────────────────────────────────────────
const CreateCard = ({ onClick }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: `2px dashed ${hovered ? T.orange : T.ruleStrong}`,
        borderRadius: T.r4,
        background: hovered ? `rgba(234,124,31,0.04)` : 'transparent',
        boxShadow: 'none',
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 220,
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'border-color 0.18s ease, background 0.18s ease',
        fontFamily: T.sans,
        gap: 0,
      }}
    >
      <span style={{ fontSize: 32, lineHeight: 1 }}>🍄</span>
      <div style={{
        fontSize: 16,
        fontWeight: 800,
        color: T.ink,
        marginTop: 10,
        fontFamily: T.sans,
      }}>
        Another idea?
      </div>
      <div style={{
        fontSize: 13,
        color: T.inkMuted,
        marginTop: 6,
        maxWidth: 200,
        lineHeight: 1.45,
        fontFamily: T.sans,
      }}>
        Describe what you're building. Cahoots will ask the right questions back.
      </div>
      <button
        style={{
          marginTop: 14,
          background: T.orange,
          color: '#fff',
          border: 'none',
          borderRadius: T.full,
          padding: '6px 16px',
          fontSize: 13,
          fontWeight: 800,
          cursor: 'pointer',
          fontFamily: T.sans,
          letterSpacing: 0.2,
          transition: 'background 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = T.orangeDark; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = T.orange; }}
      >
        + New idea
      </button>
    </div>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ onClick }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 32px',
    textAlign: 'center',
    fontFamily: T.sans,
  }}>
    <span style={{ fontSize: 48, lineHeight: 1 }}>🌱</span>
    <div style={{
      fontSize: 28,
      fontWeight: 800,
      color: T.ink,
      marginTop: 16,
      letterSpacing: -0.5,
    }}>
      Plant your first idea
    </div>
    <div style={{
      fontSize: 15,
      color: T.inkMuted,
      marginTop: 8,
      maxWidth: 340,
      lineHeight: 1.5,
    }}>
      Describe what you want to build. Cahoots will handle the discovery, planning, and engineering breakdown.
    </div>
    <button
      onClick={onClick}
      style={{
        marginTop: 24,
        background: T.orange,
        color: '#fff',
        border: 'none',
        borderRadius: T.full,
        padding: '10px 24px',
        fontSize: 15,
        fontWeight: 800,
        cursor: 'pointer',
        fontFamily: T.sans,
        letterSpacing: 0.2,
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = T.orangeDark; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = T.orange; }}
    >
      + New idea
    </button>
  </div>
);

// ─── Keyframes injection (pulse animation) ────────────────────────────────────
const injectStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('cahoots-ideas-styles')) return;
  const style = document.createElement('style');
  style.id = 'cahoots-ideas-styles';
  style.textContent = `
    @keyframes cahoots-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `;
  document.head.appendChild(style);
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const Ideas = () => {
  const { data: ideasData, isLoading, refetch } = useIdeas();
  const navigate = useNavigate();
  const { showSuccess, showError } = useApp();
  const deleteIdeaMutation = useDeleteIdea();
  const [pendingDelete, setPendingDelete] = React.useState(null);

  React.useEffect(() => {
    injectStyles();
  }, []);

  const ideas = ideasData?.items || [];

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteIdeaMutation.mutateAsync(pendingDelete.id);
      showSuccess('Idea deleted.');
      setPendingDelete(null);
      // Belt-and-suspenders: the mutation invalidates the list query, but
      // make sure the visible list reflects the deletion immediately by
      // forcing a refetch.
      await refetch();
    } catch (err) {
      showError(err?.message || 'Failed to delete idea.');
    }
  };

  return (
    <div style={{ padding: '28px 32px 40px', fontFamily: T.sans }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 18,
      }}>
        <div>
          <div style={{
            fontFamily: T.hand,
            fontSize: 26,
            color: T.orange,
            lineHeight: 1,
          }}>
            everything you're working on —
          </div>
          <h1 style={{
            fontSize: 40,
            fontWeight: 800,
            color: T.ink,
            margin: '6px 0 0',
            letterSpacing: -0.8,
            fontFamily: T.sans,
            lineHeight: 1.1,
          }}>
            Ideas{' '}
            <span style={{
              fontFamily: T.mono,
              fontSize: 20,
              color: T.inkMuted,
              fontWeight: 600,
              letterSpacing: 0,
            }}>
              · {isLoading ? '…' : ideas.length}
            </span>
          </h1>
        </div>

        <button
          onClick={() => navigate('/ideas/create')}
          style={{
            background: T.orange,
            color: '#fff',
            border: 'none',
            borderRadius: T.full,
            padding: '10px 22px',
            fontSize: 14,
            fontWeight: 800,
            cursor: 'pointer',
            fontFamily: T.sans,
            letterSpacing: 0.2,
            flexShrink: 0,
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = T.orangeDark; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = T.orange; }}
        >
          + New idea
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 18,
        }}>
          {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && ideas.length === 0 && (
        <EmptyState onClick={() => navigate('/ideas/create')} />
      )}

      {/* Ideas grid */}
      {!isLoading && ideas.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 18,
        }}>
          {ideas.map((idea) => (
            <IdeaCard
              key={idea.id}
              idea={idea}
              onClick={() => navigate(`/ideas/${idea.id}`)}
              onRequestDelete={setPendingDelete}
              deleting={deleteIdeaMutation.isPending && pendingDelete?.id === idea.id}
            />
          ))}
          <CreateCard onClick={() => navigate('/ideas/create')} />
        </div>
      )}

      <DeleteIdeaConfirmModal
        idea={pendingDelete}
        isOpen={!!pendingDelete}
        onCancel={() => !deleteIdeaMutation.isPending && setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        busy={deleteIdeaMutation.isPending}
      />
    </div>
  );
};

export default Ideas;
